import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, Loader2, X, Bot, User, FileSpreadsheet, Sparkles, MessageSquareText, Minimize2, Copy, Maximize2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { API_URL as API } from "@/lib/api";

const CHART_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

const InlineChart = ({ config }) => {
  if (!config) return null;
  let type = config.type || "bar";
  let chartData = config.data;
  const title = config.title || "";
  const dKey = config.dataKey || "value";
  const nKey = config.nameKey || "name";

  if (chartData && !Array.isArray(chartData) && chartData.labels && chartData.datasets) {
    const labels = chartData.labels;
    const values = chartData.datasets[0]?.data || [];
    chartData = labels.map((l, i) => ({ name: String(l), value: values[i] || 0 }));
  }
  if (!Array.isArray(chartData) || chartData.length === 0) return null;

  return (
    <div className="my-3 p-3 bg-card rounded-lg border" data-testid="tab-analyst-chart">
      {title && <p className="text-xs font-semibold text-foreground mb-2">{title}</p>}
      <ResponsiveContainer width="100%" height={180}>
        {type === "pie" ? (
          <PieChart>
            <Pie data={chartData} dataKey={dKey} nameKey={nKey} cx="50%" cy="50%" outerRadius={60} innerRadius={28}
              label={({ name, percent }) => `${(name || "").slice(0, 12)} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 10 }}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        ) : type === "line" ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey={nKey} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey={dKey} stroke="#FF9900" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        ) : (
          <BarChart data={chartData}>
            <XAxis dataKey={nKey} tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 11 }} />
            <Bar dataKey={dKey} fill="#FF9900" radius={[3, 3, 0, 0]}>
              {chartData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const formatMarkdown = (text) => {
  if (!text) return "";
  let cleaned = text.replace(/```json\s*\{[\s\S]*?\}\s*```/g, "").trim();
  const lines = cleaned.split("\n");
  let html = "";
  let tableRows = [];

  const flushTable = () => {
    if (tableRows.length === 0) return;
    html += '<div class="overflow-x-auto my-2"><table class="w-full border-collapse text-xs">';
    tableRows.forEach((row, ri) => {
      const tag = ri === 0 ? "th" : "td";
      const cls = ri === 0
        ? "px-2 py-1.5 bg-muted font-semibold text-foreground border border-border text-left"
        : "px-2 py-1.5 border border-border text-muted-foreground";
      html += "<tr>" + row.map((c) => `<${tag} class="${cls}">${c.replace(/\*\*/g, "")}</${tag}>`).join("") + "</tr>";
    });
    html += "</table></div>";
    tableRows = [];
  };

  let inTable = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      const cells = trimmed.slice(1, -1).split("|").map((c) => c.trim());
      if (cells.every((c) => /^[-:]+$/.test(c))) continue;
      tableRows.push(cells);
      inTable = true;
    } else {
      if (inTable) { flushTable(); inTable = false; }
      let l = trimmed;
      l = l.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      l = l.replace(/\*(.+?)\*/g, "<em>$1</em>");
      l = l.replace(/`([^`]+)`/g, '<code class="bg-muted px-1 rounded text-xs font-mono">$1</code>');
      if (l.startsWith("### ")) html += `<p class="font-bold text-sm text-foreground mt-3 mb-1">${l.slice(4)}</p>`;
      else if (l.startsWith("## ")) html += `<p class="font-bold text-base text-foreground mt-3 mb-1">${l.slice(3)}</p>`;
      else if (l.startsWith("# ")) html += `<p class="font-bold text-base text-[#FF9900] mt-3 mb-1">${l.slice(2)}</p>`;
      else if (l.startsWith("- ")) html += `<p class="ml-3 text-xs text-foreground leading-relaxed">&bull; ${l.slice(2)}</p>`;
      else if (/^\d+\.\s/.test(l)) html += `<p class="ml-3 text-xs text-foreground leading-relaxed">${l}</p>`;
      else if (l === "") html += '<div class="h-1.5"></div>';
      else html += `<p class="text-xs text-foreground leading-relaxed">${l}</p>`;
    }
  }
  if (inTable) flushTable();
  return html;
};

const TAB_SUGGESTIONS = {
  inventario: [
    "Resume el estado general del inventario de iCATA",
    "Busca productos con 12V ROJO en la descripcion",
    "Top 5 familias con mayor valor de stock",
    "Productos con stock critico que necesitan reabastecimiento",
    "Analiza la rotacion por lotes y recomienda estrategia FIFO",
  ],
  salidas: [
    "Resume las salidas y entregas recientes",
    "Compara volumen de entregas entre Manco Kapac y Plaza Bonita",
    "Familias mas entregadas y su tendencia",
    "Exporta resumen de salidas por local a Excel",
  ],
  manco_kapac: [
    "Resume el inventario de Manco Kapac",
    "Busca productos LED 5050 en esta tienda",
    "Productos con stock critico que necesitan reposicion",
    "Analiza la distribucion por lotes en Manco Kapac",
  ],
  plaza_bonita: [
    "Resume el inventario de Plaza Bonita",
    "Busca productos IP65 en esta tienda",
    "Productos con stock critico que necesitan reposicion",
    "Analiza la distribucion por lotes en Plaza Bonita",
  ],
  lotes: [
    "Resume los lotes mas antiguos con stock alto",
    "Que productos del Lote 230 aun tienen stock?",
    "Recomienda estrategia FIFO para los lotes criticos",
    "Exporta productos prioritarios FIFO a Excel",
  ],
  comparativo: [
    "Compara el inventario de ambos locales",
    "Que productos tiene Manco Kapac que no tiene Plaza Bonita?",
    "Recomendaciones de transferencia entre locales",
    "Exporta discrepancias a Excel",
  ],
  cruce: [
    "Analiza las discrepancias encontradas en el cruce",
    "Cuales son los items faltantes mas criticos",
    "Resume los hallazgos del cruce de datos",
    "Exporta las discrepancias a Excel",
  ],
  default: [
    "Resume los datos de esta pestaña",
    "Identifica alertas de stock critico",
    "Hay anomalias en los datos?",
    "Exporta un resumen a Excel",
  ],
};

const MIN_W = 360;
const MIN_H = 420;
const DEFAULT_W = 440;
const DEFAULT_H = 560;

const TabAnalystChat = ({ tabName, tabId, dataContext, dataSummary }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `tab_${tabId}_${Date.now()}`);
  const [exporting, setExporting] = useState(false);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const resizingRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    setMessages([]);
    setSessionId(`tab_${tabId}_${Date.now()}`);
  }, [tabId]);

  // Resize handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingRef.current) return;
      const { startX, startY, startW, startH } = resizingRef.current;
      const dx = startX - e.clientX;
      const dy = startY - e.clientY;
      setSize({
        w: Math.max(MIN_W, Math.min(startW + dx, window.innerWidth - 40)),
        h: Math.max(MIN_H, Math.min(startH + dy, window.innerHeight - 40)),
      });
    };
    const handleMouseUp = () => { resizingRef.current = null; document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
  }, []);

  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = { startX: e.clientX, startY: e.clientY, startW: size.w, startH: size.h };
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';
  };

  const suggestions = TAB_SUGGESTIONS[tabId] || TAB_SUGGESTIONS.default;

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/chat/tab-analyze`, {
        message: msg,
        session_id: sessionId,
        tab_context: typeof dataContext === "string" ? dataContext : JSON.stringify(dataContext || {}).slice(0, 6000),
        tab_name: tabName,
        data_summary: typeof dataSummary === "string" ? dataSummary : JSON.stringify(dataSummary || {}).slice(0, 2000),
      }, { timeout: 120000 });

      const data = res.data;
      const charts = (data.charts || []).filter((c) => c?.data?.length > 0);
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: data.response,
        charts,
        exportData: data.export_data || null,
      }]);
      if (data.session_id) setSessionId(data.session_id);
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.message || "Error de conexion";
      setMessages((prev) => [...prev, { role: "assistant", content: `Error: ${errMsg}`, isError: true }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleExport = async (exportData) => {
    setExporting(true);
    try {
      const resp = await axios.post(`${API}/api/chat/export-excel`, exportData, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `analisis_${tabId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("Error al exportar Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleCopyText = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Texto copiado")).catch(() => {});
  };

  const handleExportText = async (text) => {
    const lines = text.split("\n").filter(l => l.trim());
    const exportData = {
      title: `Analisis ${tabName}`,
      columns: ["Linea", "Contenido"],
      rows: lines.map((l, i) => [i + 1, l.replace(/\*\*/g, "").replace(/[#*`]/g, "").trim()]),
    };
    await handleExport(exportData);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const clearChat = () => {
    setMessages([]);
    setSessionId(`tab_${tabId}_${Date.now()}`);
  };

  const toggleFullSize = () => {
    if (size.w > DEFAULT_W + 100) {
      setSize({ w: DEFAULT_W, h: DEFAULT_H });
    } else {
      setSize({ w: Math.min(800, window.innerWidth - 40), h: Math.min(700, window.innerHeight - 40) });
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 h-13 w-13 rounded-full shadow-lg bg-[#FF9900] hover:bg-[#E68A00] text-white p-0 flex items-center justify-center gap-0"
        data-testid="tab-analyst-toggle"
      >
        <Sparkles className="h-5 w-5" />
      </Button>
    );
  }

  return (
    <Card ref={panelRef}
      className="fixed bottom-6 right-6 z-50 flex flex-col shadow-2xl border-2 border-[#FF9900]/30 rounded-xl overflow-hidden bg-background"
      style={{ width: size.w, height: size.h }}
      data-testid="tab-analyst-panel">

      {/* Resize handle (top-left corner) */}
      <div
        onMouseDown={startResize}
        className="absolute top-0 left-0 w-5 h-5 cursor-nw-resize z-20 group"
        data-testid="tab-analyst-resize"
      >
        <div className="absolute top-1 left-1 w-2.5 h-2.5 border-t-2 border-l-2 border-[#FF9900]/40 group-hover:border-[#FF9900] rounded-tl transition-colors" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#232F3E] text-white shrink-0">
        <div className="flex items-center gap-2.5">
          <Sparkles className="h-4 w-4 text-[#FF9900]" />
          <div>
            <p className="text-sm font-semibold leading-none tracking-tight">Analista IA</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{tabName}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={toggleFullSize} className="h-7 w-7 p-0 text-gray-400 hover:text-white hover:bg-white/10" title="Expandir/Reducir">
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={clearChat} className="h-7 w-7 p-0 text-gray-400 hover:text-white hover:bg-white/10" data-testid="tab-analyst-clear" title="Limpiar chat">
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 w-7 p-0 text-gray-400 hover:text-white hover:bg-white/10" data-testid="tab-analyst-minimize" title="Minimizar">
            <Minimize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4" data-testid="tab-analyst-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center gap-4 pt-6">
            <div className="w-12 h-12 rounded-xl bg-[#FF9900]/10 flex items-center justify-center">
              <Bot className="w-6 h-6 text-[#FF9900]" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">Analista de iCATA</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[300px]">
                Analizo los datos reales de <strong>{tabName}</strong>. Pregunta lo que necesites o exporta a Excel.
              </p>
            </div>
            <div className="w-full space-y-2">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="w-full text-left text-xs text-muted-foreground bg-muted/60 border border-border rounded-lg px-3 py-2.5 hover:bg-[#FF9900]/10 hover:border-[#FF9900]/40 hover:text-foreground transition-all"
                  data-testid={`tab-analyst-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`tab-analyst-msg-${i}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 rounded-lg bg-[#FF9900]/10 flex items-center justify-center shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-[#FF9900]" />
              </div>
            )}
            <div className={`max-w-[88%] ${msg.role === "user"
              ? "bg-[#FF9900] text-white rounded-2xl rounded-tr-sm px-4 py-2.5"
              : msg.isError
                ? "bg-rose-50 border border-rose-200 rounded-2xl rounded-tl-sm px-4 py-3"
                : "bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm"
            }`}>
              {msg.role === "user" ? (
                <p className="text-sm leading-relaxed">{msg.content}</p>
              ) : (
                <>
                  <div className="analyst-response" dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }} />
                  {msg.charts?.map((chart, ci) => <InlineChart key={ci} config={chart} />)}
                  <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/50">
                    <Button size="sm" variant="ghost" onClick={() => handleCopyText(msg.content)}
                      className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground">
                      <Copy className="h-3 w-3" /> Copiar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleExportText(msg.content)}
                      disabled={exporting}
                      className="h-6 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground">
                      <FileSpreadsheet className="h-3 w-3" /> Excel
                    </Button>
                    {msg.exportData && (
                      <Button size="sm" variant="outline" onClick={() => handleExport(msg.exportData)}
                        disabled={exporting}
                        className="h-6 px-2 text-[10px] gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        data-testid="tab-analyst-export-btn">
                        {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
                        Datos Excel
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2.5" data-testid="tab-analyst-loading">
            <div className="w-7 h-7 rounded-lg bg-[#FF9900]/10 flex items-center justify-center shrink-0">
              <Bot className="w-3.5 h-3.5 text-[#FF9900]" />
            </div>
            <div className="bg-card border border-border rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#FF9900]" />
                <span className="text-xs text-muted-foreground">Analizando datos de iCATA...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input - Textarea (2-3 lines) */}
      <div className="px-4 py-3 border-t border-border shrink-0 bg-muted/20">
        <div className="flex items-end gap-2">
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Pregunta sobre los datos de iCATA... (ej: 'busca 12V ROJO', 'analiza cinta LED 5050')"
            disabled={loading}
            rows={3}
            className="flex-1 min-h-[60px] max-h-[100px] px-3 py-2 text-sm rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#FF9900]/40 focus:border-[#FF9900] resize-none"
            data-testid="tab-analyst-input" />
          <Button onClick={() => sendMessage()} disabled={loading || !input.trim()}
            className="h-9 w-9 p-0 bg-[#FF9900] hover:bg-[#E68A00] text-white shrink-0 mb-0.5"
            data-testid="tab-analyst-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default TabAnalystChat;
